/**
 * Aprovar e rejeitar, na tela (H12).
 *
 * ── A escolha do sobrevivente é estado da TELA, e nasce vazia ─────────────
 *
 * A Armadilha da task: *"pré-selecionar o sobrevivente. O revisor confirma sem
 * ler, o cliente errado é mesclado, e desfazer exige split — que cria uma
 * terceira identidade."*
 *
 * Por isso `sobrevivente` começa `null` e não há valor padrão em lugar nenhum:
 * nem "o mais antigo", nem "o de maior score", nem "o primeiro da lista". O
 * botão de aprovar só existe depois de alguém escolher, e enquanto não escolhe
 * ele diz o que falta em vez de ficar cinza e mudo.
 *
 * ── E a confirmação existe AQUI, ao contrário do encerramento ─────────────
 *
 * No encerrar conversa (D08) não há diálogo, porque a ação tem volta pelo mesmo
 * botão. Aqui tem: desfazer um merge exige split, e o split cria uma identidade
 * NOVA — o cliente não volta ao que era. Confirmação em ação reversível treina
 * a ignorar; em ação como esta, ela é o único momento em que a pessoa lê o que
 * vai acontecer.
 */

import { useCallback, useState } from 'react';
import type { ClienteDaApi } from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';

export type EstadoDaDecisao =
  | { readonly nome: 'parado' }
  | { readonly nome: 'enviando' }
  | { readonly nome: 'falhou'; readonly erro: unknown };

export interface Decisao {
  readonly estado: EstadoDaDecisao;
  /** `null` até alguém escolher. NÃO tem padrão — ver o cabeçalho. */
  readonly sobrevivente: string | null;
  readonly escolherSobrevivente: (clienteId: string) => void;
  /** `true` quando a tela está pedindo a confirmação final. */
  readonly confirmando: boolean;
  readonly pedirConfirmacao: () => void;
  readonly cancelar: () => void;
  readonly aprovar: () => void;
  readonly rejeitar: (motivo: string) => void;
}

export function useDecisao(
  api: ClienteDaApi | null,
  candidatoId: string | null,
  invalidar: (chaves: readonly (readonly unknown[])[]) => void,
  aoDecidir: () => void,
): Decisao {
  const [estado, definirEstado] = useState<EstadoDaDecisao>({ nome: 'parado' });
  const [sobrevivente, definirSobrevivente] = useState<string | null>(null);
  const [confirmando, definirConfirmando] = useState(false);

  const limpar = useCallback(() => {
    definirEstado({ nome: 'parado' });
    definirSobrevivente(null);
    definirConfirmando(false);
  }, []);

  const concluir = useCallback(() => {
    limpar();
    /*
     * A fila E a métrica, as duas.
     *
     * Decidir tira o item da fila e move a taxa de vínculo automático — que é o
     * número usado para calibrar o limiar. Invalidar só a fila deixaria a
     * métrica mostrando um denominador que a própria decisão acabou de mudar.
     */
    invalidar([chaves.candidatosDeMerge({}), chaves.metricaDeIdentidade()]);
    aoDecidir();
  }, [limpar, invalidar, aoDecidir]);

  const aprovar = useCallback(() => {
    if (!api || !candidatoId || !sobrevivente) return;

    definirEstado({ nome: 'enviando' });
    void api
      .aprovarCandidatoDeMerge({
        caminho: { id: candidatoId },
        corpo: { sobrevivente_id: sobrevivente },
        idempotencyKey: chaveDoComando(),
      })
      .then(concluir)
      .catch((erro: unknown) => definirEstado({ nome: 'falhou', erro }));
  }, [api, candidatoId, sobrevivente, concluir]);

  const rejeitar = useCallback(
    (motivo: string) => {
      if (!api || !candidatoId) return;

      definirEstado({ nome: 'enviando' });
      void api
        .rejeitarCandidatoDeMerge({
          caminho: { id: candidatoId },
          corpo: { motivo },
          idempotencyKey: chaveDoComando(),
        })
        .then(concluir)
        .catch((erro: unknown) => definirEstado({ nome: 'falhou', erro }));
    },
    [api, candidatoId, concluir],
  );

  return {
    estado,
    sobrevivente,
    escolherSobrevivente: definirSobrevivente,
    confirmando,
    pedirConfirmacao: () => definirConfirmando(true),
    cancelar: () => definirConfirmando(false),
    aprovar,
    rejeitar,
  };
}

/** Chave por COMANDO, não por tentativa — mesma regra do envio (B30). */
function chaveDoComando(): string {
  return globalThis.crypto?.randomUUID?.() ?? `decisao-${Date.now()}-${Math.random()}`;
}
